import React, { useMemo, useState } from 'react';
import { Alert, Col, Form, Row, Spinner, Stack, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from 'components/base/Button';
import { useBranchContext } from 'providers/BranchProvider';
import axiosInstance from 'utils/axiosInterceptors';
import { formatDateFilter } from 'utils/formats';
import {
  TSalesInvoiceDailyProduct,
  TSalesInvoiceDailyProducts
} from 'utils/types';
import { getUnitLabel } from 'utils/unitOptions';

const formatMoney = (value: number | null | undefined): string =>
  `₱${Number(value ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const formatQty = (value: number): string =>
  value.toLocaleString(undefined, { maximumFractionDigits: 2 });

const formatDisplayDate = (dateKey: string): string => {
  if (!dateKey) return '—';
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const shiftDate = (dateKey: string, deltaDays: number): string => {
  const [year, month, day] = dateKey.split('-').map(Number);
  const date = new Date(year, month - 1, day + deltaDays);
  return formatDateFilter(date);
};

const productRowKey = (row: TSalesInvoiceDailyProduct): string =>
  row.productId != null ? `id:${row.productId}` : `name:${row.productName}`;

const ProductsTab = () => {
  const { selectedBranch } = useBranchContext();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const todayKey = formatDateFilter(new Date());
  const [date, setDate] = useState(queryParams.get('date') || todayKey);
  const [search, setSearch] = useState('');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const syncDate = (nextDate: string) => {
    setDate(nextDate);
    setExpandedKey(null);
    const nextParams = new URLSearchParams(location.search);
    nextParams.set('date', nextDate);
    navigate({ search: nextParams.toString() }, { replace: true });
  };

  const {
    data,
    isLoading,
    isFetching,
    isError
  } = useQuery({
    queryKey: ['salesInvoiceDailyProducts', selectedBranch?.branchDomain, date],
    enabled: !!selectedBranch?.branchDomain,
    queryFn: async () => {
      const { data: result } =
        await axiosInstance.get<TSalesInvoiceDailyProducts>(
          `/api/v1/${selectedBranch?.branchDomain}/salesInvoices/products`,
          { params: { date } }
        );
      return result;
    }
  });

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    const products = data?.products ?? [];
    if (!term) return products;

    return products.filter(row => {
      const invoiceMatch = row.invoices.some(
        invoice =>
          invoice.invoiceNumber.toLowerCase().includes(term) ||
          (invoice.soldTo ?? '').toLowerCase().includes(term)
      );

      return (
        row.productName.toLowerCase().includes(term) ||
        (row.brand ?? '').toLowerCase().includes(term) ||
        (row.category ?? '').toLowerCase().includes(term) ||
        invoiceMatch
      );
    });
  }, [data?.products, search]);

  const toggleRow = (key: string) => {
    setExpandedKey(current => (current === key ? null : key));
  };

  return (
    <>
      <Stack
        direction="horizontal"
        className="justify-content-between align-items-start mb-3 flex-wrap gap-3"
      >
        <div>
          <p className="mb-0 text-body-tertiary fs-9">
            Submitted invoices only. Expand a product to see the invoices it
            was sold on.
          </p>
        </div>
        <Stack direction="horizontal" gap={2} className="align-items-center flex-wrap">
          <Button
            variant="phoenix-secondary"
            className="px-2"
            onClick={() => syncDate(shiftDate(date, -1))}
            aria-label="Previous day"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </Button>
          <Form.Control
            type="date"
            value={date}
            onChange={e => {
              if (e.target.value) syncDate(e.target.value);
            }}
            style={{ width: 160 }}
          />
          <Button
            variant="phoenix-secondary"
            className="px-2"
            onClick={() => syncDate(shiftDate(date, 1))}
            aria-label="Next day"
            disabled={date >= todayKey}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </Button>
          {date !== todayKey && (
            <Button variant="phoenix-secondary" onClick={() => syncDate(todayKey)}>
              Today
            </Button>
          )}
        </Stack>
      </Stack>

      <Row className="g-3 mb-3 align-items-end">
        <Col md={4} lg={3}>
          <Form.Group>
            <Form.Label className="fs-9">Search products</Form.Label>
            <Form.Control
              type="search"
              placeholder="Product, brand, invoice..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md="auto" className="ms-md-auto">
          <div className="text-body-tertiary fs-9">
            {formatDisplayDate(date)}
            {!isLoading && data && (
              <span className="ms-2 fw-semibold text-body">
                · {data.productCount} products · {data.invoiceCount} invoices ·{' '}
                {formatMoney(data.totalSales)}
              </span>
            )}
          </div>
        </Col>
      </Row>

      {!selectedBranch?.branchDomain ? (
        <Alert variant="warning">Select a branch to view products sold.</Alert>
      ) : isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" />
        </div>
      ) : isError ? (
        <Alert variant="danger">Failed to load products sold for this day.</Alert>
      ) : filteredProducts.length === 0 ? (
        <p className="text-body-tertiary mb-0">
          {search.trim()
            ? 'No products matched.'
            : 'No products sold on submitted invoices for this day.'}
        </p>
      ) : (
        <div className={isFetching ? 'opacity-75' : undefined}>
          <Table responsive hover className="fs-9">
            <thead>
              <tr>
                <th style={{ width: 36 }} />
                <th>Product</th>
                <th>Category</th>
                <th>Unit</th>
                <th className="text-end">Qty</th>
                <th className="text-end">Returned</th>
                <th className="text-end">Invoices</th>
                <th className="text-end">Sales</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map(row => {
                const key = productRowKey(row);
                const isExpanded = expandedKey === key;

                return (
                  <React.Fragment key={key}>
                    <tr
                      role="button"
                      onClick={() => toggleRow(key)}
                      className={isExpanded ? 'table-active' : undefined}
                    >
                      <td className="text-body-tertiary">
                        <FontAwesomeIcon
                          icon={isExpanded ? faChevronUp : faChevronDown}
                        />
                      </td>
                      <td>
                        {row.productId != null ? (
                          <Link
                            to={`/product-list/product-details/${row.productId}`}
                            className="text-decoration-none fw-semibold"
                            onClick={event => event.stopPropagation()}
                          >
                            {row.productName}
                          </Link>
                        ) : (
                          <span className="fw-semibold">{row.productName}</span>
                        )}
                        {row.brand && (
                          <div className="text-body-tertiary fs-10">{row.brand}</div>
                        )}
                      </td>
                      <td className="text-body-tertiary">{row.category || '—'}</td>
                      <td>{row.unitOfMeasure ? getUnitLabel(row.unitOfMeasure) : '—'}</td>
                      <td className="text-end">{formatQty(row.quantity)}</td>
                      <td className="text-end">
                        {row.quantityReturned > 0
                          ? formatQty(row.quantityReturned)
                          : '—'}
                      </td>
                      <td className="text-end">{row.invoiceCount}</td>
                      <td className="text-end text-nowrap">
                        {formatMoney(row.totalSales)}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} className="bg-body-highlight">
                          <Table size="sm" className="mb-0 fs-9">
                            <thead>
                              <tr>
                                <th>Invoice</th>
                                <th>Sold To</th>
                                <th>Unit</th>
                                <th className="text-end">Qty</th>
                                <th className="text-end">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {row.invoices.map(invoice => (
                                <tr key={invoice.salesInvoiceId}>
                                  <td>
                                    <Link
                                      to={`/sales-details/${invoice.salesInvoiceId}`}
                                      className="text-decoration-none fw-semibold"
                                    >
                                      {invoice.invoiceNumber}
                                    </Link>
                                  </td>
                                  <td>{invoice.soldTo || '—'}</td>
                                  <td>
                                    {invoice.unitOfMeasure
                                      ? getUnitLabel(invoice.unitOfMeasure)
                                      : '—'}
                                  </td>
                                  <td className="text-end">
                                    {formatQty(invoice.quantity)}
                                  </td>
                                  <td className="text-end text-nowrap">
                                    {formatMoney(invoice.amount)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            {data && !search.trim() && (
              <tfoot>
                <tr className="fw-semibold">
                  <td />
                  <td>Total</td>
                  <td />
                  <td />
                  <td className="text-end">{formatQty(data.totalQuantity)}</td>
                  <td />
                  <td className="text-end">{data.invoiceCount}</td>
                  <td className="text-end text-nowrap">
                    {formatMoney(data.totalSales)}
                  </td>
                </tr>
              </tfoot>
            )}
          </Table>
        </div>
      )}
    </>
  );
};

export default ProductsTab;
